import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession, signOut } from 'next-auth/react';
import { cx } from 'cva';
import { Badge, SVG, CADConfirmationModal } from '@inkd/ui';
import { useCartState } from '@web/utils';
import { CardsAfterDarkContext } from '@web/context/cardsAfterDark';
import { FlagToggleContext } from '@web/context/flagToggle';
import InkdLogo from './complexSVGs/inkdLogo';

// Define all translations outside the component
const translations = {
  en: {
    signIn: 'Sign In',
    dashboard: 'Dashboard',
    logOut: 'Log Out',
    switchLanguage: 'Cambiar a Español', // Switch to Spanish
    categories: 'All Categories',
    personalization: 'Cancel Personalization',
    cart: 'Cart',
    helpInfo: 'Help & Info',
    kioskLocations: 'Kiosk locations',
    needSupport: 'Need Support?',
    privacyPolicyTerms: 'Privacy Policy and Terms of Service',
    language: 'Language',
    cancelPersonalizationAria: 'Cancel personalization of card',
    cardsAfterDark: 'Switch to cards "after dark" 😈',
    switchToRegular: 'Switch to Regular',
  },
  es: {
    signIn: 'Iniciar Sesión',
    dashboard: 'Panel de Control',
    logOut: 'Cerrar Sesión',
    switchLanguage: 'Switch to English',
    categories: 'Todas las Categorías',
    personalization: 'Cancelar Personalización',
    cart: 'Carrito',
    helpInfo: 'Ayuda e Información',
    kioskLocations: 'Ver ubicaciones',
    needSupport: '¿Necesitas ayuda?',
    privacyPolicyTerms: 'Política de Privacidad y Términos del Servicio',
    language: 'Idioma',
    cancelPersonalizationAria: 'Cancelar personalización de la tarjeta',
    cardsAfterDark: 'Tarjetas Traviesas',
    switchToRegular: 'Cambiar a Regular',
  },
};

interface BrowsingFlowNavbarProps {
  flow: 'browsing';
  handleCancelPersonalization?: never;
}
interface PersonalizationFlowNavbarProps {
  flow: 'personalization';
  handleCancelPersonalization: () => void;
}
interface CheckoutFlowNavbarProps {
  flow: 'checkout';
  handleCancelPersonalization?: never;
}
interface AccountFlowNavbarProps {
  flow: 'account';
  handleCancelPersonalization?: never;
}
interface VerificationFlowNavbarProps {
  flow: 'verification';
  handleCancelPersonalization?: never;
}

type NavbarProps =
  | BrowsingFlowNavbarProps
  | PersonalizationFlowNavbarProps
  | CheckoutFlowNavbarProps
  | AccountFlowNavbarProps
  | VerificationFlowNavbarProps;

/** Detects if the page is running inside a WebView (e.g. Wix mobile app) vs a regular browser */
function isWebView(): boolean {
  if (typeof window === 'undefined') return false; // SSR guard
  const ua = window.navigator.userAgent;
  // Matches Android WebView (wv token) or iOS WebView (AppleWebKit without Safari)
  return (
    /wv|WebView/i.test(ua) ||
    /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua)
  );
}

export function Navbar({ flow, handleCancelPersonalization }: NavbarProps) {
  const router = useRouter();
  const cardsAfterDarkContext = useContext(CardsAfterDarkContext);
  const languageContext = useContext(FlagToggleContext);
  const { status } = useSession();
  const isLoggedIn = status === 'authenticated';

  // Track the hamburger menu state (left side)
  const [menuOpen, setMenuOpen] = useState(false);

  const [isCADModalOpen, setIsCADModalOpen] = useState(false);

  // `mounted` + `inWebView` prevent SSR/client hydration mismatch
  const [mounted, setMounted] = useState(false);
  const [inWebView, setInWebView] = useState(false);
  useEffect(() => {
    setInWebView(isWebView());
    setMounted(true);
  }, []);

  const isCardsAfterDark = cardsAfterDarkContext?.isCardsAfterDark ?? false;
  const isFlagToggle = languageContext?.isFlagToggle ?? false;

  // NEW: Detect if we are on any Warner Brothers branded page
  const isWBPage = router.pathname.startsWith('/wb');

  const handleCADToggle = useCallback(() => {
    if (!isCardsAfterDark) {
      setIsCADModalOpen(true);
    } else {
      cardsAfterDarkContext?.setCardsAfterDark(false);
    }
  }, [isCardsAfterDark, cardsAfterDarkContext]);

  const handleCADModalConfirm = useCallback(() => {
    cardsAfterDarkContext?.setCardsAfterDark(true);
    setIsCADModalOpen(false);
  }, [cardsAfterDarkContext]);

  const handleCADModalCancel = useCallback(() => {
    setIsCADModalOpen(false);
  }, []);

  // Toggle function for the language (flag toggle)
  const toggleLanguage = useCallback(() => {
    if (
      languageContext &&
      typeof languageContext.setFlagToggle === 'function'
    ) {
      languageContext.setFlagToggle(!languageContext.isFlagToggle);
    }
  }, [languageContext]);

  // Determine the current language
  const currentLang = isFlagToggle ? 'es' : 'en';

  // Determine background gradient based on flow or Cards After Dark
  const flowBackgrounds: Record<NavbarProps['flow'], string> = {
    browsing: 'bg-gradient-blue-to-yellow',
    personalization: 'bg-gradient-blue-to-purple',
    checkout: 'bg-gradient-lightblue-to-blue',
    account: 'bg-gradient-blue-to-yellow',
    verification: 'bg-gradient-blue-to-yellow',
  };
  const navBackground = isCardsAfterDark
    ? 'bg-gradient-blue-to-purple'
    : flowBackgrounds[flow];

  // Refs for the left-hamburger dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Route to different URLs based on whether we're in the Wix WebView or a regular browser.
  // We gate on `mounted` to avoid SSR/client hydration mismatches.
  const locationsHref =
    mounted && inWebView
      ? 'https://www.inkdgreetings.com/locationsforapp'
      : 'https://www.inkdgreetings.com/locations';

  const contactHref =
    mounted && inWebView
      ? 'https://www.inkdgreetings.com/contactforapp'
      : 'https://www.inkdgreetings.com/contact';

  const termsHref =
    mounted && inWebView
      ? 'https://www.inkdgreetings.com/termsforapp'
      : 'https://www.inkdgreetings.com/terms';

  return (
    <>
      <nav
        aria-label='Main navigation'
        className={cx('sticky top-0 z-[100]', navBackground)}
      >
        <div className='relative mx-auto flex max-w-screen-md items-center justify-between px-2 py-3 sm:p-3'>
          {/* Left Section: Logo + left hamburger */}
          <div className='flex items-center space-x-2'>
            <NavbarLogo
              flow={flow}
              handleCancelPersonalization={handleCancelPersonalization}
            />

            <button
              type='button'
              onClick={() => setMenuOpen(prev => !prev)}
              ref={buttonRef}
              className='
                inline-flex 
                h-12 w-12
                items-center justify-center 
                rounded-lg bg-white p-2 
                text-sm text-gray-500
                hover:bg-gray-100
                focus:outline-none focus:ring-2 focus:ring-gray-200
              '
              aria-label='Open menu'
            >
              <svg
                className='h-6 w-6'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 17 14'
              >
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M1 1h15M1 7h15M1 13h15'
                />
              </svg>
            </button>
          </div>

          {/* UPDATED: CAD toggle is now hidden on all Warner Brothers pages */}
          {flow !== 'account' &&
          flow !== 'verification' &&
          !isWBPage &&
          (!cardsAfterDarkContext?.maxRating ||
            cardsAfterDarkContext?.maxRating > 3) ? (
            <NavbarToggles
              flow={flow}
              isCardsAfterDark={isCardsAfterDark}
              isFlagToggle={isFlagToggle}
              onCardsAfterDarkToggle={handleCADToggle}
              currentLang={currentLang}
            />
          ) : null}

          {/* Right Section => user icon + cart or personalization cancel */}
          <NavbarActions
            flow={flow}
            handleCancelPersonalization={handleCancelPersonalization}
            currentLang={currentLang}
          />

          {/* Left Hamburger Dropdown */}
          {menuOpen && (
            <div
              ref={dropdownRef}
              className='
                absolute left-0 top-16
                z-50 mt-2
                flex w-56
                flex-col
                overflow-hidden rounded-lg border border-gray-100
                bg-white
                py-1
                shadow-lg ring-1 ring-black/5
              '
            >
              {/* Group 1: Account */}
              {!isLoggedIn && flow !== 'verification' ? (
                <Link
                  href='/account/sign-in'
                  className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
                >
                  {translations[currentLang].signIn}
                </Link>
              ) : isLoggedIn ? (
                <Link
                  href='/account/dashboard'
                  className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
                >
                  {translations[currentLang].dashboard}
                </Link>
              ) : null}

              {/* Group 2: Help & Info */}
              <div className='my-1 border-t border-gray-100' />
              <p className='px-4 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400'>
                {translations[currentLang].helpInfo}
              </p>
              <a
                href={locationsHref}
                target={mounted && inWebView ? undefined : '_blank'}
                rel='noopener noreferrer'
                className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
              >
                {translations[currentLang].kioskLocations}
              </a>
              <a
                href={contactHref}
                target={mounted && inWebView ? undefined : '_blank'}
                rel='noopener noreferrer'
                className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
              >
                {translations[currentLang].needSupport}
              </a>
              <a
                href={termsHref}
                target={mounted && inWebView ? undefined : '_blank'}
                rel='noopener noreferrer'
                className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
              >
                {translations[currentLang].privacyPolicyTerms}
              </a>

              {/* Language Toggle */}
              {flow !== 'verification' ? (
                <>
                  <div className='my-1 border-t border-gray-100' />
                  <p className='px-4 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400'>
                    {translations[currentLang].language}
                  </p>
                  <button
                    onClick={toggleLanguage}
                    className='flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
                  >
                    <span>{translations[currentLang].switchLanguage}</span>
                  </button>
                </>
              ) : null}
            </div>
          )}
        </div>
      </nav>
      {isCADModalOpen && (
        <CADConfirmationModal
          version='web'
          handleConfirm={handleCADModalConfirm}
          handleCancel={handleCADModalCancel}
          spanish={currentLang === 'es'}
        />
      )}
    </>
  );
}

/** Logo or Cancel Button */
function NavbarLogo({
  flow,
  handleCancelPersonalization,
}: {
  flow: NavbarProps['flow'];
  handleCancelPersonalization?: () => void;
}) {
  if (flow === 'personalization' && handleCancelPersonalization) {
    return (
      <button onClick={handleCancelPersonalization}>
        <InkdLogo />
      </button>
    );
  }

  return (
    <Link href='/' aria-label='Home screen'>
      <InkdLogo />
    </Link>
  );
}

interface NavbarTogglesProps {
  flow: NavbarProps['flow'];
  isCardsAfterDark: boolean;
  isFlagToggle: boolean;
  onCardsAfterDarkToggle: () => void;
  currentLang: 'en' | 'es';
}

/** Cards After Dark Toggle */
function NavbarToggles({
  isCardsAfterDark,
  onCardsAfterDarkToggle,
  currentLang,
}: NavbarTogglesProps) {
  const labelText = isCardsAfterDark
    ? translations[currentLang].switchToRegular
    : translations[currentLang].cardsAfterDark;

  return (
    <div
      className='
        flex
        min-w-0 flex-col 
        items-center
        space-y-1
        sm:flex-row sm:space-x-2
        sm:space-y-0
      '
    >
      <p
        className='text-center text-[12px] sm:whitespace-nowrap sm:text-[13px] md:text-[16px]'
        style={{
          fontFamily: "LazyDog, 'Courier New', Courier, monospace",
        }}
      >
        {labelText}
      </p>
      <label className='switch small' aria-label='Toggle Cards After Dark mode'>
        <input
          type='checkbox'
          checked={isCardsAfterDark}
          onChange={onCardsAfterDarkToggle}
        />
        <span className='slider' />
      </label>
    </div>
  );
}

interface NavbarActionsProps {
  flow: NavbarProps['flow'];
  handleCancelPersonalization?: () => void;
  currentLang: 'en' | 'es';
}

/** Right side: user icon first, then cart or personalization cancel */
function NavbarActions({
  flow,
  handleCancelPersonalization,
  currentLang,
}: NavbarActionsProps) {
  // Return nothing for verification flow
  if (flow === 'verification') {
    return null;
  }

  return (
    <div className='flex items-center space-x-4'>
      {/* Always show the User Icon first, except in verification flow */}
      <UserMenu currentLang={currentLang} />

      {/* Then show browsing or personalization actions */}
      {flow === 'browsing' && <BrowsingFlow currentLang={currentLang} />}
      {flow === 'personalization' && handleCancelPersonalization && (
        <button
          className='flex items-center justify-center rounded-full bg-white p-2 sm:p-1'
          aria-label={translations[currentLang].cancelPersonalizationAria}
          onClick={handleCancelPersonalization}
        >
          <SVG name='X' className='h-8 w-8' />
        </button>
      )}
    </div>
  );
}

interface BrowsingFlowProps {
  currentLang: 'en' | 'es';
}

/** Cart icon (browsing flow) */
function BrowsingFlow({ currentLang }: BrowsingFlowProps) {
  const { cart } = useCartState();
  const cartQuantity = useMemo(() => cart.length, [cart]);

  if (cartQuantity === 0) {
    return null;
  }

  return (
    <Link
      href='/checkout/cart'
      className='relative flex items-center justify-center rounded-full bg-white p-2'
    >
      <span className='sr-only'>{translations[currentLang].cart}</span>
      <SVG name='Cart' className='h-8 w-8' />
      {cartQuantity > 0 && (
        <Badge
          aria-label={`${cartQuantity} item${
            cartQuantity === 1 ? '' : 's'
          } in cart`}
          className='absolute -right-1 -top-1'
        >
          {cartQuantity}
        </Badge>
      )}
    </Link>
  );
}

interface UserMenuProps {
  currentLang: 'en' | 'es';
}

/** The user icon on the right side: toggles a small dropdown */
function UserMenu({ currentLang }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const { status } = useSession();
  const isLoggedIn = status === 'authenticated';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className='relative' ref={dropRef}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-label='Open user menu'
        className='
          inline-flex 
          h-12 w-12
          items-center justify-center 
          rounded-lg bg-white p-2 
          text-sm text-gray-500
          hover:bg-gray-100
          focus:outline-none focus:ring-2 focus:ring-gray-200
        '
      >
        <SVG name='User Icon' className='h-8 w-8' />
      </button>

      {open && (
        <div
          className='
            absolute right-0 top-full
            z-50 mt-2
            flex w-44
            flex-col
            overflow-hidden rounded-lg border border-gray-100
            bg-white
            shadow-lg ring-1 ring-black/5
          '
        >
          {!isLoggedIn ? (
            <Link
              href='/account/sign-in'
              className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
            >
              {translations[currentLang].signIn}
            </Link>
          ) : (
            <>
              <Link
                href='/account/dashboard'
                className='px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
              >
                {translations[currentLang].dashboard}
              </Link>
              <button
                type='button'
                onClick={() => signOut({ callbackUrl: '/' })}
                className='px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900'
              >
                {translations[currentLang].logOut}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
